import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import { authService } from '../services/authService'; 

const TwoFactorSetup: React.FC = () => {
    const [qrCode, setQrCode] = useState<string | null>(null);
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    /**
     * Chiede al backend di generare un nuovo QR code.
     */
    const handleGenerate = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await authService.generate2FA();
            setQrCode(response.qr_code_image);
        } catch (err) {
            setError('Errore durante la generazione del QR code. Riprova più tardi.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Invia il codice a 6 cifre per verificare e abilitare la 2FA.
     */
    const handleVerify = async () => {
        setLoading(true);
        setError('');
        try {
            await authService.enable2FA(code);
            setSuccess('2FA abilitata con successo! Dal prossimo login ti verrà richiesto il codice.');
            setQrCode(null); // Nasconde il QR code dopo il successo
        } catch (err: any) {
            setError(err.response?.data?.error || 'Codice non valido. Riprova.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6">Autenticazione a Due Fattori (2FA)</Typography>
            <Divider sx={{ my: 2 }} />
            
            {success && <Alert severity="success">{success}</Alert>}
            
            {!qrCode && !success && (
                <>
                    <Typography variant="body2" color="text.secondary">
                        Aumenta la sicurezza del tuo account. Abilita la 2FA per richiedere un codice di verifica ad ogni accesso.
                    </Typography>
                    <Button onClick={handleGenerate} variant="contained" sx={{ mt: 2 }} disabled={loading}>
                        {loading ? <CircularProgress size={24} /> : 'Abilita 2FA'}
                    </Button>
                </>
            )}

            {qrCode && (
                <Box mt={2}>
                    <Typography variant="subtitle1" fontWeight="bold">1. Scansiona il QR Code</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Usa la tua app di autenticazione (es. Google Authenticator) per scansionare questa immagine.
                    </Typography>
                    <Box
                        component="img"
                        src={qrCode}
                        alt="QR Code per 2FA"
                        sx={{ mt: 1, p: 1, borderRadius: 2, border: '1px solid #ddd', backgroundColor: 'white' }}
                    />
                    
                    <Typography variant="subtitle1" fontWeight="bold" mt={3}>2. Verifica e Attiva</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Inserisci il codice a 6 cifre che vedi nell'app per completare la configurazione.
                    </Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                        <TextField
                            label="Codice di verifica"
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1 }}
                            inputProps={{ maxLength: 6 }}
                        />
                        <Button onClick={handleVerify} variant="contained" disabled={loading || code.length !== 6}>
                            {loading ? <CircularProgress size={24} /> : 'Verifica e Attiva'}
                        </Button>
                    </Box>
                </Box>
            )}

            {error && !success && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
        </Paper>
    );
};

export default TwoFactorSetup;